import React, { useEffect, useMemo, useState } from "react";
import { Button, Input, Spinner, Table } from "reactstrap";

import { listarGatilhos, salvarGatilhos } from "../../../services/despachoApi";
import { Aviso, EstadoTabela, ModalHub, extractError, fieldInputStyle } from "../_documentosFrete/comum";
import { descreverGatilho, gatilhosParaSalvar, mesclarGatilhos, validarGatilhos } from "./regras";

// Gatilhos de fechamento por transportadora (PO1-2377): por horário (HH:mm,
// o cron do backend fecha o lote do dia) e/ou por volume (fecha ao atingir N
// envios). Uma linha por transportadora do embarcador, com os dois campos
// independentes; os dois vazios = "Sem gatilho" = fecha só à mão.
//
// Contrato do PUT /despacho/gatilhos (QA 12/10): o body é o ESTADO COMPLETO
// desejado (pode ser []); o que não vier é desativado. Por isso a tela monta as
// linhas só com os gatilhos ATIVOS do GET e sempre manda a lista inteira.
// Sem hub.operacao.despacho.editar a lista é só leitura.

export default function GatilhosModal({ transportadoras = [], podeEditar, onClose }) {
  // O que veio do servidor (GET ou resposta do PUT) e o que o usuário editou,
  // separados: a lista de transportadoras pode chegar depois do GET, e refazer
  // a mescla não pode apagar o que já foi digitado.
  const [salvos, setSalvos] = useState([]);
  const [edicoes, setEdicoes] = useState({}); // { [idTransportadora]: { horario?, volume? } }
  const [carregando, setCarregando] = useState(true);
  const [erroCarga, setErroCarga] = useState("");
  const [erros, setErros] = useState({});
  const [salvando, setSalvando] = useState(false);
  const [aviso, setAviso] = useState(null);
  const [alterou, setAlterou] = useState(false);

  useEffect(() => {
    let ativo = true;
    listarGatilhos()
      .then((g) => ativo && setSalvos(g))
      .catch((e) => ativo && setErroCarga(extractError(e, "Não foi possível carregar os gatilhos.")))
      .then(() => ativo && setCarregando(false));
    return () => {
      ativo = false;
    };
  }, []);

  const linhas = useMemo(
    () => mesclarGatilhos(transportadoras, salvos).map((l) => (edicoes[l.idTransportadora] ? { ...l, ...edicoes[l.idTransportadora] } : l)),
    [transportadoras, salvos, edicoes]
  );

  // Lê o valor ANTES do setState com updater (event pooling do React 16).
  const set = (idTransportadora, campo) => (e) => {
    const valor = e.target.value;
    setEdicoes((atual) => ({ ...atual, [idTransportadora]: { ...(atual[idTransportadora] || {}), [campo]: valor } }));
    setErros((atual) => (atual[idTransportadora] ? { ...atual, [idTransportadora]: undefined } : atual));
  };

  // "Sem gatilho": limpa os dois campos da transportadora.
  const limparLinha = (idTransportadora) => {
    setEdicoes((atual) => ({ ...atual, [idTransportadora]: { horario: "", volume: "" } }));
    setErros((atual) => (atual[idTransportadora] ? { ...atual, [idTransportadora]: undefined } : atual));
  };

  const salvar = async () => {
    if (salvando || !podeEditar) return;
    const locais = validarGatilhos(linhas);
    setErros(locais);
    if (Object.keys(locais).length) {
      setAviso({ tom: "erro", texto: "Confira os gatilhos marcados." });
      return;
    }
    setSalvando(true);
    setAviso(null);
    try {
      // Sempre a lista completa, mesmo vazia: é o estado desejado do embarcador.
      const resposta = await salvarGatilhos(gatilhosParaSalvar(linhas));
      setSalvos(resposta);
      setEdicoes({});
      setAlterou(true);
      setAviso({ tom: "ok", texto: "Gatilhos salvos." });
    } catch (e) {
      setAviso({ tom: "erro", texto: extractError(e, "Não foi possível salvar os gatilhos.") });
    } finally {
      setSalvando(false);
    }
  };

  const fechar = () => {
    if (salvando) return;
    onClose(alterou);
  };

  const travado = !podeEditar || salvando;

  return (
    <ModalHub titulo="Gatilhos de fechamento" icone="tim-icons icon-time-alarm" onClose={fechar} maxWidth={860}>
      <p className="docs-frete__dica" style={{ marginBottom: "0.75rem" }}>
        Por horário, o lote aberto da transportadora fecha sozinho no horário informado; por volume, fecha ao atingir a quantidade de envios. Os dois
        podem valer juntos. Deixe os campos vazios para a transportadora fechar só pelo botão "Fechar lote".
      </p>
      {erroCarga ? <Aviso tom="erro">{erroCarga}</Aviso> : null}
      {aviso ? <Aviso tom={aviso.tom}>{aviso.texto}</Aviso> : null}
      <EstadoTabela loading={carregando} vazio={!carregando && !erroCarga && !linhas.length} textoCarregando="Carregando os gatilhos..." textoVazio="Nenhuma transportadora vinculada ao embarcador." />
      {!carregando && linhas.length ? (
        <Table className="docs-frete__tabela hub-thead-accent despacho-lotes__gatilhos" size="sm" responsive>
          <thead className="text-primary">
            <tr>
              <th>Transportadora</th>
              <th>Fecha às</th>
              <th className="is-number">Fecha com (envios)</th>
              <th>Resumo</th>
              {podeEditar ? <th aria-label="Ações" /> : null}
            </tr>
          </thead>
          <tbody>
            {linhas.map((l) => (
              <tr key={l.idTransportadora}>
                <td>
                  <strong>{l.transportadora}</strong>
                  {erros[l.idTransportadora] ? <span className="docs-frete__erro-campo">{erros[l.idTransportadora]}</span> : null}
                </td>
                <td>
                  <Input
                    type="time"
                    bsSize="sm"
                    aria-label={`Horário de ${l.transportadora}`}
                    id={`gatilho-horario-${l.idTransportadora}`}
                    value={l.horario}
                    disabled={travado}
                    invalid={Boolean(erros[l.idTransportadora]) && Boolean(l.horario)}
                    onChange={set(l.idTransportadora, "horario")}
                    style={fieldInputStyle}
                  />
                </td>
                <td className="is-number">
                  <Input
                    type="number"
                    min={1}
                    step={1}
                    bsSize="sm"
                    aria-label={`Quantidade de envios de ${l.transportadora}`}
                    id={`gatilho-volume-${l.idTransportadora}`}
                    value={l.volume}
                    placeholder="Sem limite"
                    disabled={travado}
                    invalid={Boolean(erros[l.idTransportadora]) && l.volume !== ""}
                    onChange={set(l.idTransportadora, "volume")}
                    style={fieldInputStyle}
                  />
                </td>
                <td>
                  <span className="docs-frete__dica" style={{ marginTop: 0 }} data-testid={`gatilho-resumo-${l.idTransportadora}`}>
                    {descreverGatilho(l)}
                  </span>
                </td>
                {podeEditar ? (
                  <td>
                    {l.horario || l.volume ? (
                      <Button color="default" size="sm" outline onClick={() => limparLinha(l.idTransportadora)} disabled={salvando} aria-label={`Sem gatilho para ${l.transportadora}`}>
                        Sem gatilho
                      </Button>
                    ) : null}
                  </td>
                ) : null}
              </tr>
            ))}
          </tbody>
        </Table>
      ) : null}
      <div className="docs-frete__modal-rodape">
        <Button color="default" size="sm" outline onClick={fechar} disabled={salvando}>
          Fechar
        </Button>
        {podeEditar ? (
          <Button color="primary" size="sm" onClick={salvar} disabled={salvando || carregando || Boolean(erroCarga) || !linhas.length}>
            {salvando ? <Spinner size="sm" style={{ marginRight: 6 }} /> : null}
            Salvar gatilhos
          </Button>
        ) : null}
      </div>
    </ModalHub>
  );
}
